import React, { useState, useEffect, useRef } from "react";
import styled from "@emotion/styled";
import { Stack, Input, Box, Typography, useTheme, Theme } from "@mui/material";

import { Micro, Reset, Send, ReadyRecord, Recording } from "../../icons";
import { EliceIconButton } from "../icon-button";
import AssistantAvatar from "./AssistantAvatar";
import UserAvatar from "./UserAvatar";
import TypingIndicator from "./TypingIndicator";

export type EliceMessageItem = { role: "system" | "user" | "assistant"; content: string }

export interface EliceChatProps {
  children: React.ReactNode;
  messages: EliceMessageItem[];
  height?: string;
  isLoadingMessage?: boolean;
  CustomAssistantAvatar?: React.ReactNode;
}

export type AudioStep = "ready" | "waiting" | "recording";


const EliceChat = ({
  messages = [],
  isLoadingMessage,
  children,
  height,
  CustomAssistantAvatar,
}: EliceChatProps) => {
  const theme = useTheme();
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isLoadingMessage]);

  return (
    <StyledChatContainer theme={theme} height={height}>
      <EliceChat.MessageList
        messages={messages}
        isLoadingMessage={isLoadingMessage}
        scrollRef={scrollRef}
        CustomAssistantAvatar={CustomAssistantAvatar}
      />
      {children}
    </StyledChatContainer>
  );
};

// ✅ 메시지 리스트 컴포넌트
EliceChat.MessageList = ({
  messages,
  isLoadingMessage,
  scrollRef,
  height,
  CustomAssistantAvatar,
}: {
  messages: { role: "system" | "user" | "assistant"; content: string }[];
  isLoadingMessage?: boolean;
  scrollRef: React.RefObject<HTMLDivElement>;
  height?: string;
  CustomAssistantAvatar?: React.ReactNode;
}) => {
  const theme = useTheme();

  return (
    <StyledScrollBarStack theme={theme} height={height}>
      {messages.length === 0 ? (
        <Typography color={theme.palette.text.disabled} fontSize="2rem" fontWeight={700}>
          AI와 대화를 시작해 보세요.
        </Typography>
      ) : (
        <>
          {messages.map((msg, index) => {
            if (msg.role === "system") return null;
            const isAssistant = msg.role === "assistant";

            return (
              <Stack
                key={index}
                direction={isAssistant ? "row" : "row-reverse"}
                gap="0.5rem"
                alignItems="center"
              >
                <Stack flexShrink={0}>
                  {isAssistant ? (CustomAssistantAvatar ?? <AssistantAvatar />) : <UserAvatar />}
                </Stack>
                <StyledMessage isAssistant={isAssistant} theme={theme}>
                  {msg.content}
                </StyledMessage>
              </Stack>
            );
          })}
          {isLoadingMessage && <TypingIndicator />}
          <div ref={scrollRef} />
        </>
      )}
    </StyledScrollBarStack>
  );
};

EliceChat.InputArea = ({
  placeHolder,
  value,
  onSend,
  onReset,
  onRecord,
  onTranscribingChange,
}: {
  placeHolder: string,
  value: string;
  onSend: () => void;
  onReset: () => void;
  onRecord?: () => void;
  onTranscribingChange?: (value: boolean) => void;
}) => {
  const theme = useTheme();
  const [inputValue, setInputValue] = useState(value);
  const handleSend = () => {
    if (inputValue.trim()) {
      onSend();
      setInputValue("");
    }
  };
  const onChangeValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  }
  return (
    <Stack
      direction="row"
      minHeight="3.875rem"
      bgcolor={theme.palette.grey[50]}
      borderRadius="1.125rem"
      p="0.5rem 1rem"
      gap="0.625rem"
    >
      <Stack flexGrow={1} maxHeight="14.625rem">
        <StyledInput
          value={inputValue}
          onChange={onChangeValue}
          placeholder={placeHolder}
          theme={theme}
          multiline
        />
      </Stack>
      {onRecord && (
        <EliceIconButton
          onClick={() => {
            onRecord();
            onTranscribingChange?.(true);
          }}
        >
          <Micro />
        </EliceIconButton>
      )}
      <EliceIconButton onClick={onReset}>
        <Reset />
      </EliceIconButton>
      <EliceIconButton onClick={handleSend} disabled={!inputValue.trim()}>
        <Send />
      </EliceIconButton>
    </Stack>
  );
};

const SoundWaveVisualizer = ({ analyser }: { analyser: AnalyserNode | null }) => {
  const theme = useTheme();
  const TOTAL_BARS = 17;
  const [filledBars, setFilledBars] = useState(0);

  useEffect(() => {
    if (!analyser) return;

    analyser.fftSize = 128; // ✅ FFT 크기 줄여 반응속도 개선
    const dataArray = new Uint8Array(analyser.frequencyBinCount);
    let prevVolume = 0;

    const updateVolume = () => {
      analyser.getByteFrequencyData(dataArray);
      const averageVolume = dataArray.reduce((acc, val) => acc + val, 0) / dataArray.length;

      // ✅ 더 높은 민감도 설정 (3배 증폭)
      const scaledVolume = Math.min(averageVolume * 3, 255);

      // ✅ 더 높은 가중치를 현재 값에 적용 (빠르게 반응)
      const smoothedVolume = prevVolume * 0.5 + scaledVolume * 0.5;
      prevVolume = smoothedVolume;

      // ✅ TOTAL_BARS에 맞게 변환
      const newFilledBars = Math.floor((smoothedVolume / 255) * TOTAL_BARS);

      setFilledBars((prev) => Math.round(prev * 0.6 + newFilledBars * 0.4)); // ✅ 더 빠르게 반응

      requestAnimationFrame(updateVolume);
    };

    updateVolume();

    return () => setFilledBars(0);
  }, [analyser]);

  return (
    <Stack alignItems="center" justifyContent="center" width="100%" bgcolor="transparent">
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        p="6px 36px 7.5px 31px"
        width="100%"
        gap="11px"
        bgcolor={theme.palette.grey[50]}
      >
        {Array(TOTAL_BARS)
          .fill(0)
          .map((_, idx) => (
            <WaveBar key={idx} isFilled={idx < filledBars} theme={theme} />
          ))}
      </Box>
    </Stack>
  );
};


EliceChat.AudioArea = ({
  onRecord,
  onTranscribingChange,
  isLoadingMessage,
  setAudioFile,
}: {
  onRecord?: () => void;
  onTranscribingChange?: (value: boolean) => void;
  isLoadingMessage?: boolean;
  setAudioFile: (file: File | null) => void;
}) => {
  const theme = useTheme();
  const [recordingStep, setRecordingStep] = useState<AudioStep>("ready");
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunks = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);

  const BUTTON_TEXT = {
    ready: "녹음 시작",
    recording: "녹음 중지",
    waiting: "AI 응답 대기 중...",
  };

  const startRecording = async () => {
    try {
      const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = audioStream;
      audioChunks.current = [];

      mediaRecorderRef.current = new MediaRecorder(audioStream);

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.current.push(event.data);
        }
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunks.current, { type: "audio/wav" });
        const file = new File([audioBlob], `recording-${Date.now()}.wav`, { type: "audio/wav" });

        setAudioFile(file);
      };

      // ✅ AudioContext 생성 및 오디오 분석기 연결
      audioContextRef.current = new AudioContext();
      analyserRef.current = audioContextRef.current.createAnalyser();
      sourceRef.current = audioContextRef.current.createMediaStreamSource(audioStream);
      sourceRef.current.connect(analyserRef.current);
      analyserRef.current.fftSize = 256;

      mediaRecorderRef.current.start();
      setRecordingStep("recording");
      onRecord?.();
      onTranscribingChange?.(true);
    } catch (error) {
      console.error("🎤 마이크 권한을 허용해주세요.", error);
    }
  };

  /**
   * ⏹ 녹음 중지
   */
  const stopRecording = () => {
    mediaRecorderRef.current?.stop();

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }

    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    setRecordingStep("waiting");
    onTranscribingChange?.(false);
  };

  /**
   * ⏳ AI 응답 완료 시 `ready` 상태로 복귀
   */
  useEffect(() => {
    if (!isLoadingMessage && recordingStep === "waiting") {
      setRecordingStep("ready");
    }
  }, [isLoadingMessage, recordingStep]);
  return (
    <Stack
      direction="column"
      minHeight="70px"
      bgcolor={theme.palette.grey[50]}
      padding="12px 9px 12px 8px"
      borderRadius="18px"
      alignItems="center"
      justifyContent="center"
    >
      {recordingStep === "recording" && <SoundWaveVisualizer analyser={analyserRef.current} />}
      <AudioChatButton onClick={recordingStep === "ready" ? startRecording : stopRecording} step={recordingStep}>
        {recordingStep === "ready" ? <ReadyRecord /> : <Recording />}
        <Typography variant="h1">{BUTTON_TEXT[recordingStep]}</Typography>
      </AudioChatButton>
    </Stack>
  );
};

export default EliceChat;

const StyledChatContainer = styled(Stack) <{ theme: Theme, height?: string }>`
  height: fit-content;
  height: ${({ height }) => height ?? '100%'};
  max-height: 100%;
  min-width: 30rem;
  max-width: 100%;
  border: 2px solid ${({ theme }) => theme.palette.grey[300]};
  border-radius: 1.5rem;
  padding: 23px;
  gap: 1.75rem;
`;

const StyledScrollBarStack = styled(Stack) <{ theme: Theme, height?: string }>`
  flex: 1;
  overflow-y: auto;
  height: ${({ height }) => height ?? '100%'};
  border-radius: 1.125rem;
  padding-right: 1rem;
  ::-webkit-scrollbar {
    width: 0.25rem;
  }
  ::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.palette.grey[200]};
    border-radius: 0.5rem;
  }
  ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.palette.primary.main};
    border-radius: 0.5rem;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.palette.secondary.main};
  }
`;

const StyledInput = styled(Input) <{ theme: Theme }>`
  width: 100%;
  height: 100%;
  border: none !important;
  background-color: transparent;
  font-size: 1.375rem;
  font-weight: 700;
  outline: none;
  resize: none;
  color: ${({ theme }) => theme.palette.secondary.main};
  ::placeholder {
    color: ${({ theme }) => theme.palette.grey[300]};
  }
  
  ::before {
    border: none;
  }
  :hover {
    ::before {
      border: none;
    }
  }
  ::after {
    border-bottom: none !important;
  }
  
   ::-webkit-scrollbar {
    width: 0.25rem;
  }
  ::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.palette.grey[200]};
    border-radius: 0.5rem;
  }
  ::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.palette.primary.main};
    border-radius: 0.5rem;
  }
  ::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.palette.secondary.main};
  }
`;

const StyledMessage = styled(Typography) <{ isAssistant: boolean, theme: Theme }>`
  color: ${({ isAssistant, theme }) =>
    isAssistant ? theme.palette.text.secondary : theme.palette.secondary.main};
  font-size: 1rem;
  font-weight: ${({ isAssistant }) => (isAssistant ? 500 : 700)};
  background-color: ${({ isAssistant, theme }) =>
    isAssistant ? theme.palette.grey[50] : theme.palette.primary.light};
  border-radius: 1rem;
  padding: 0.75rem;
  margin-bottom: 16px;
  white-space: pre-wrap;
`;

const WaveBar = styled(Box) <{ isFilled: boolean; theme: Theme }>`
  width: 11px;
  height: 34px;
  border-radius: 11px;
  background-color: ${({ isFilled, theme }) =>
    isFilled ? theme.palette.primary.main : theme.palette.grey[200]};
  transition: background-color 0.1s ease-in-out;
`;

const AudioChatButton = styled.button<{ step: AudioStep }>`
  display: flex;
  width: 100%;
  border: none;
  height: 36px;
  padding: 12px 20px 12px 17px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  flex-shrink: 0;
  border-radius: 16px;
  color: ${({ step }) => step === "ready" ? "#fff" : "#FF5D58"};
  background: ${({ step }) => step === "ready" ? "#FF5D58" : "#FBE0E0"};
`
